import {
  Button,
  Divider,
  Flex,
  List,
  LoadingOverlay,
  Modal,
  Radio,
  Stack,
  Text,
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

type QuizAnswerOption = { index: number; text: string };
type QuizQuestion = {
  questionId: number;
  description: string;
  answers: QuizAnswerOption[];
};

export default function QuizComponent({ c, opened, onClose, callback }: any) {
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cambia quando cambia l’elenco domande → forza re-init del form
  const formKey = useMemo(
    () => (opened ? questions.map(q => q.questionId).join('_') : 'closed'),
    [opened, questions]
  );

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {},
    // Validazione calcolata al momento del submit, sulla base di `questions`
    validate: values => {
      const errors: Record<string, string | null> = {};
      for (const q of questions) {
        const key = `question-${q.questionId}`;
        const questionValue = (values as Record<string, any>)[key];
        if (
          questionValue === undefined ||
          questionValue === null ||
          questionValue === ''
        ) {
          errors[key] = 'Questa domanda è obbligatoria';
        }
      }
      return errors;
    },
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/services/getQuiz/${c.id}`,
        {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }
      const result = await response.json();
      const qs: QuizQuestion[] = Array.isArray(result?.data) ? result.data : [];
      setQuestions(qs);

      // Prepara initialValues vuoti per tutte le domande
      const init: Record<string, any> = {};
      for (const q of qs) init[`question-${q.questionId}`] = '';
      form.setValues(init);
      form.resetDirty(init);
      form.clearErrors();
    } catch (error: any) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'Errore caricamento quiz',
        autoClose: false,
        message: error?.message ?? 'Impossibile caricare le domande',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (opened) fetchData();
    else {
      form.reset();
      setQuestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const submit = async (values: any) => {
    setIsSubmitting(true);
    try {
      const { hasErrors } = form.validate();
      if (hasErrors) {
        setIsSubmitting(false);
        return;
      }

      // === QUI la parte importante: costruiamo l’OGGETTO che il backend si aspetta ===
      const answersObj: Record<string, string> = {};
      for (const q of questions) {
        const key = `question-${q.questionId}`;
        answersObj[key] = String(values[key]); // es. "2"
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/services/submitQuiz/${c.id}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-type': 'application/json; charset=UTF-8' },
          body: JSON.stringify({ answers: answersObj }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const r = await response.json();
      notifications.show({
        color: r?.data?.passed ? 'teal' : 'red',
        withBorder: true,
        radius: 'lg',
        autoClose: false,
        message:
          r?.message ??
          (r?.data?.passed ? 'Quiz superato' : 'Quiz non superato'),
      });

      callback?.();
      onClose?.();
    } catch (error: any) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'Errore invio quiz',
        autoClose: false,
        message: error?.message ?? 'Invio non riuscito',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Data formattata correttamente
  const date = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dateStr = `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={'Test di fine corso'}
      fullScreen
    >
      {loading ? (
        <>Loading...</>
      ) : (
        <>
          <Text>{c.name}</Text>
          <Text>Data: {dateStr}</Text>
          <Divider mt='md' mb='md' />
          <form
            key={formKey}
            onSubmit={form.onSubmit(values => submit(values))}
            style={{ position: 'relative' }}
          >
            <LoadingOverlay visible={isSubmitting} />
            <List type='ordered' spacing='lg'>
              {questions.map(q => (
                <List.Item key={`q-${q.questionId}`}>
                  <Radio.Group
                    {...form.getInputProps(`question-${q.questionId}`)}
                    label={q.description}
                    required
                  >
                    <Stack mt='md'>
                      {q.answers.map(a => (
                        <Radio
                          key={`r-${q.questionId}-${a.index}`}
                          value={String(a.index)} // String: ok per il backend (parseInt)
                          label={a.text}
                        />
                      ))}
                    </Stack>
                  </Radio.Group>
                </List.Item>
              ))}
            </List>
            <Flex justify='flex-end' mt='md'>
              <Button
                type='submit'
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Invia
              </Button>
            </Flex>
          </form>
        </>
      )}
    </Modal>
  );
}
